'use client';

import { useState } from 'react';

const SUBJECTS = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'bug', label: 'Bug Report' },
];

export default function ContactForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('general');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), subject, message: message.trim() }),
      });

      const data = await res.json();

      if (!res.ok) {
        setStatus('error');
        setErrorMsg(data.error || 'Something went wrong.');
        return;
      }

      setStatus('success');
      setName('');
      setEmail('');
      setSubject('general');
      setMessage('');
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className="p-8 bg-[#E7F5FF] rounded-xl text-center">
        <h3 className="text-[22px] font-semibold text-[#1a1a2e] mb-2">Message Sent</h3>
        <p className="text-[16px] text-gray-600 mb-4">
          Thank you for reaching out. We&apos;ll get back to you as soon as possible.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="text-[#0068D6] font-medium hover:underline"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label htmlFor="contact-name" className="block text-[14px] font-medium text-[#1a1a2e] mb-1.5">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          id="contact-name"
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full h-[44px] px-3 border border-gray-300 rounded-lg text-[16px] text-[#1a1a2e] outline-none focus:border-[#0068D6] focus:ring-1 focus:ring-[#0068D6] transition-colors"
          placeholder="Your name"
        />
      </div>

      <div>
        <label htmlFor="contact-email" className="block text-[14px] font-medium text-[#1a1a2e] mb-1.5">
          Email <span className="text-red-500">*</span>
        </label>
        <input
          id="contact-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full h-[44px] px-3 border border-gray-300 rounded-lg text-[16px] text-[#1a1a2e] outline-none focus:border-[#0068D6] focus:ring-1 focus:ring-[#0068D6] transition-colors"
          placeholder="you@example.com"
        />
      </div>

      <div>
        <label htmlFor="contact-subject" className="block text-[14px] font-medium text-[#1a1a2e] mb-1.5">
          Subject <span className="text-red-500">*</span>
        </label>
        <select
          id="contact-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full h-[44px] px-3 border border-gray-300 rounded-lg text-[16px] text-[#1a1a2e] outline-none focus:border-[#0068D6] focus:ring-1 focus:ring-[#0068D6] transition-colors bg-white"
        >
          {SUBJECTS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="contact-message" className="block text-[14px] font-medium text-[#1a1a2e] mb-1.5">
          Message <span className="text-red-500">*</span>
        </label>
        <textarea
          id="contact-message"
          required
          minLength={10}
          maxLength={2000}
          rows={6}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full px-3 py-3 border border-gray-300 rounded-lg text-[16px] text-[#1a1a2e] outline-none focus:border-[#0068D6] focus:ring-1 focus:ring-[#0068D6] transition-colors resize-vertical"
          placeholder="How can we help?"
        />
        <p className="text-[13px] text-gray-400 mt-1">{message.length}/2000</p>
      </div>

      {status === 'error' && (
        <p className="text-[14px] text-red-600">{errorMsg}</p>
      )}

      <button
        type="submit"
        disabled={status === 'sending'}
        className="w-full h-[48px] bg-[#0068D6] hover:bg-[#0066DD] disabled:opacity-60 text-white font-semibold rounded-lg text-[16px] transition-colors"
        style={{ fontFamily: 'Roboto' }}
      >
        {status === 'sending' ? 'Sending...' : 'Send Message'}
      </button>
    </form>
  );
}
